import React, { PropsWithChildren, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import {
    isNewStatsigProvider,
    isNewStatsigSDK
} from '~/common/Statsig/hybridStatsig';
import {
    useHybridStatsig,
    type HybridStatsigProps
} from '~/common/Statsig/useHybridStatsig';
import { areStatsigUsersDifferent } from '~/common/Statsig/statsigUserUtils';

export type HBStatsigProviderInstance = {
    // Used for debugging purposes, to identify the provider in the console
    name?: string;
    // Silence the warning about this provider being nested
    silenceIsNestedWarning?: boolean;
};

type HBStatsigProviderProps = PropsWithChildren<HybridStatsigProps>;

export const HBStatsigProvider = observer(
    ({ children, ...hybridStatsigProps }: HBStatsigProviderProps) => {
        const { StatsigProvider, statsigProviderProps, client } =
            useHybridStatsig(hybridStatsigProps);

        const updateUserIfNeeded = useCallback(() => {
            if (isNewStatsigSDK()) {
                const { user: clientContextUser } = client.getContext();
                const statsigUserRequiresUpdate = areStatsigUsersDifferent(
                    clientContextUser,
                    statsigProviderProps.user
                );

                if (statsigUserRequiresUpdate) {
                    client.updateUserAsync(statsigProviderProps.user);
                }
            }
        }, [statsigProviderProps.user, client]);

        useEffect(() => {
            updateUserIfNeeded();
        }, [updateUserIfNeeded]);

        if (isNewStatsigProvider(StatsigProvider)) {
            return (
                <StatsigProvider client={client} loadingComponent={<></>}>
                    {children}
                </StatsigProvider>
            );
        } else {
            return (
                <StatsigProvider {...statsigProviderProps}>
                    {children}
                </StatsigProvider>
            );
        }
    }
);
